import {
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  CancelOutlined,
  KeyboardArrowDownRounded,
  KeyboardArrowRightRounded
} from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import { useQueryStringNumber } from '../../../hooks/useQueryString';
import Breadcrumb from '../../Breadcrumb';
import { getPipingProjectDocumentsAsync } from '../../MasterData/Document/documentSlice';
import { getProjectAsync } from '../../Project/projectSlice';

export default function PipingReviewDocument() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const projectId = useQueryStringNumber({ query: 'projectId' });

  const { selectedProject } = useSelector((state: RootState) => state.projects);
  const { pipingProjectDocuments } = useSelector(
    (state: RootState) => state.documents
  );

  const [openedDocumentIds, setOpenedDocumentIds] = useState<number[]>([]);

  useEffect(() => {
    if (
      pipingProjectDocuments[0] &&
      pipingProjectDocuments[0].projectDocuments.length > 0
    ) {
      setOpenedDocumentIds([pipingProjectDocuments[0].id]);
    }
  }, [pipingProjectDocuments]);

  useEffect(() => {
    if (projectId) {
      dispatch(getPipingProjectDocumentsAsync(projectId));
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectAsync(projectId));
    }
  }, []);

  function handleOpenDocument(id: number): void {
    const open = getStatus(id, openedDocumentIds);
    if (open) {
      // If the document is already open, remove it from the list
      setOpenedDocumentIds(
        openedDocumentIds.filter((openedDocumentId) => openedDocumentId !== id)
      );
    } else {
      setOpenedDocumentIds([...openedDocumentIds, id]);
    }
  }

  function getStatus(value: number, selectedItems: number[]): boolean {
    return selectedItems.indexOf(value) !== -1;
  }

  if (!projectId) {
    return <Redirect to="/projects" />;
  }
  return (
    <div className={classes.root}>
      <Breadcrumb
        name="relatedDocuments"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          discipline: 'pip',
          createBulkPlanning: 'PIP'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('bulkPlanning.common.relatedDocuments')}
      </Typography>
      <Paper className={classes.card}>
        <Typography variant="h6" display="inline">
          {t('bulkPlanning.labels.documentList')}
        </Typography>
        <Typography display="inline">
          {' '}
          ({pipingProjectDocuments.length} document
          {pipingProjectDocuments.length > 1 && 's'})
        </Typography>
        <Divider className={classes.divider} />
        {pipingProjectDocuments.map((document, index) => {
          const count = document.projectDocuments.length;
          const open = getStatus(document.id, openedDocumentIds);
          return (
            <div key={index}>
              <div className={classes.flexSpace}>
                <div className={classes.icon}>
                  {count > 0 ? (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDocument(document.id)}
                    >
                      {open ? (
                        <KeyboardArrowDownRounded />
                      ) : (
                        <KeyboardArrowRightRounded />
                      )}
                    </IconButton>
                  ) : (
                    <Tooltip title={t('bulkPlanning.common.notFoundDocument') as string} arrow>
                      <CancelOutlined color="error" />
                    </Tooltip>
                  )}
                </div>
                {count > 0 && (
                  <Chip size="small" variant="outlined" label={count} />
                )}
                <Typography>{document.name}</Typography>
              </div>
              <Collapse in={open}>
                <TableContainer style={{ overflowY: 'hidden' }}>
                  <Table className={classes.table} size="small">
                    <TableHead>
                      <TableRow className={classes.tableHead}>
                        <TableCell>{t('bulkPlanning.tableHeaders.documentNo')}</TableCell>
                        <TableCell>{t('bulkPlanning.tableHeaders.documentName')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {document.projectDocuments
                        .slice() // Create a copy to avoid mutating the original array
                        .sort((a, b) => a.no.localeCompare(b.no))
                        .map((document, index) => {
                          return (
                            <TableRow className={classes.tableBody} key={index}>
                              <TableCell>{document.no}</TableCell>
                              <TableCell>{document.name}</TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                  <Divider className={classes.my} />
                </TableContainer>
              </Collapse>
            </div>
          );
        })}
      </Paper>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { margin: theme.spacing(1.5, 0, 12, 0) },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    flexSpace: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center'
    },
    icon: {
      width: 32,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    table: {
      marginLeft: theme.spacing(5),
      width: `calc(100% - ${theme.spacing(5)}px)`
    },
    tableHead: {
      '& > *': {
        padding: theme.spacing(0.75, 1)
      }
    },
    tableBody: {
      '& > *': {
        borderBottom: 'unset',
        padding: theme.spacing(0.5, 1)
      }
    },
    my: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    }
  })
);
